// src/components/history/TravelHistory.js
import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Select, Button, Badge, ConfirmDialog } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { TRUCK_STATUSES } from '../../data/store';
import { matchToRoad, getRoute } from '../../services/geocoding';
import { syncOneTruck } from '../../services/syncTravelHistory';
import { loadCredentialsFromBackend, TraccarConfig } from '../../services/traccar';
import { BASE_URL, getToken } from '../../services/api';

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
}
function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-PH', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDuration(minutes) {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
// Some older synced entries may not have a resolved address — show
// coordinates instead of leaving it blank.
function displayAddress(ev) {
  if (ev.address) return ev.address;
  if (ev.lat != null && ev.lng != null) return `${ev.lat.toFixed(5)}, ${ev.lng.toFixed(5)}`;
  return 'Unknown location';
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// GPS trackers wobble slightly even when a truck is parked/idle at one spot
// — each tiny wobble gets recorded as its own history entry with a
// slightly different lat/lng. Connecting every single one chronologically
// turns that noise into a tangled "mesh" instead of a clean path. This
// collapses runs of points that haven't actually moved (within
// minMeters of the last KEPT point) down to one, for the LINE only —
// every original event still gets its own marker/popup, nothing is hidden,
// this only smooths what gets drawn as the connecting route.
function simplifyPathForLine(sortedEvents, minMeters = 40) {
  if (sortedEvents.length === 0) return [];
  const kept = [sortedEvents[0]];
  for (let i = 1; i < sortedEvents.length; i++) {
    const last = kept[kept.length - 1];
    const cur = sortedEvents[i];
    if (haversineMeters(last.lat, last.lng, cur.lat, cur.lng) >= minMeters) {
      kept.push(cur);
    }
  }
  return kept;
}

// Smoothly moves a Leaflet marker along a multi-point road path over
// durationMs — same technique used for the truck icon on Live Tracking —
// so the "current position" indicator here travels along the actual
// street when a new entry arrives, instead of just popping to the new spot.
function animateMarkerAlongPath(marker, path, durationMs) {
  if (!path || path.length < 2) {
    if (path?.length === 1) marker.setLatLng(path[0]);
    return () => {};
  }
  const cumDist = [0];
  for (let i = 1; i < path.length; i++) {
    cumDist.push(cumDist[i - 1] + haversineMeters(path[i - 1][0], path[i - 1][1], path[i][0], path[i][1]));
  }
  const total = cumDist[cumDist.length - 1] || 1;
  let cancelled = false;
  let rafId = null;
  const start = performance.now();

  function frame(now) {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / durationMs);
    const targetDist = t * total;
    let idx = 0;
    while (idx < cumDist.length - 2 && cumDist[idx + 1] < targetDist) idx++;
    const segStart = cumDist[idx];
    const segEnd = cumDist[idx + 1] ?? segStart;
    const segT = segEnd > segStart ? (targetDist - segStart) / (segEnd - segStart) : 0;
    const p0 = path[idx];
    const p1 = path[idx + 1] ?? p0;
    marker.setLatLng([p0[0] + (p1[0] - p0[0]) * segT, p0[1] + (p1[1] - p0[1]) * segT]);
    if (t < 1) rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);
  return () => { cancelled = true; if (rafId) cancelAnimationFrame(rafId); };
}

// Vertical Timeline component
function Timeline({ events, trucks, isMobile, onEventClick, selectedEventId }) {
  if (events.length === 0) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--textFaint)', fontSize: 14 }}>
      No travel history found for the selected filters.
    </div>
  );

  const grouped = events.reduce((acc, ev) => {
    const date = formatDate(ev.timestamp);
    if (!acc[date]) acc[date] = [];
    acc[date].push(ev);
    return acc;
  }, {});

  return (
    <div style={{ padding: '8px 0' }}>
      {Object.entries(grouped).map(([date, dayEvents]) => (
        <div key={date}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 16px' }}>
            <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '1px', padding: '4px 12px', background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 20 }}>{date}</div>
            <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
          </div>

          <div style={{ position: 'relative', paddingLeft: 40 }}>
            <div style={{ position: 'absolute', left: 15, top: 0, bottom: 0, width: 2, background: 'var(--border)' }} />

            {dayEvents.map((ev, idx) => {
              const truck = trucks.find(t => t.id === ev.truckId);
              const isIdle = ev.type === 'idle';
              const dotColor = isIdle ? '#f59e0b' : '#10b981';
              const isLast = idx === dayEvents.length - 1;
              const isSelected = ev.id === selectedEventId;
              const clickable = ev.lat != null && ev.lng != null;

              return (
                <div key={ev.id} style={{ position: 'relative', marginBottom: isLast ? 0 : 16 }}>
                  <div style={{
                    position: 'absolute', left: -32, top: 14, width: 14, height: 14, borderRadius: '50%',
                    background: dotColor, border: `2px solid var(--bgCard)`,
                    boxShadow: isIdle ? `0 0 8px ${dotColor}66` : 'none', zIndex: 1,
                  }} />
                  {idx > 0 && <div style={{ position: 'absolute', left: -26, top: -16, height: 16, width: 2, background: 'var(--border)' }} />}

                  <div
                    onClick={() => clickable && onEventClick?.(ev)}
                    style={{
                      background: isSelected ? 'var(--bgActive)' : 'var(--bgCard)',
                      border: `1px solid ${isSelected ? 'rgba(249,115,22,0.4)' : isIdle ? 'rgba(245,158,11,0.25)' : 'var(--border)'}`,
                      borderRadius: 12, padding: '12px 16px',
                      cursor: clickable ? 'pointer' : 'default',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14 }}>{isIdle ? '⏸' : '▶'}</span>
                        <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--textPrimary)' }}>
                          {isIdle ? 'Truck Idle' : 'Moving'}
                        </span>
                        {truck && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: truck.color }} />
                            <span style={{ fontSize: 11, color: 'var(--textMuted)' }}>{truck.plateNumber}</span>
                          </div>
                        )}
                        {clickable && (
                          <span style={{ fontSize: 10, color: isSelected ? '#f97316' : 'var(--textFaint)' }}>🗺️</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {ev.duration && <Badge color={isIdle ? '#f59e0b' : '#10b981'}>{isIdle ? '⏱ ' : '🏃 '}{formatDuration(ev.duration)}</Badge>}
                        <span style={{ fontSize: 11, color: 'var(--textFaint)', fontFamily: 'monospace' }}>{formatTime(ev.timestamp)}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 12 }}>📍</span>
                          <span style={{ fontSize: 13, color: 'var(--textSecondary)' }}>{displayAddress(ev)}</span>
                        </div>
                        {ev.speed > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--textMuted)' }}>Speed: <span style={{ color: '#10b981', fontWeight: 600 }}>{ev.speed} km/h</span></div>
                        )}
                        {isIdle && <div style={{ marginTop: 6, fontSize: 11, color: '#f59e0b', fontWeight: 500 }}>⚠ Vehicle stopped at this location</div>}
                      </div>
                      {!isMobile && (
                        <div style={{ fontSize: 10, color: 'var(--textFaint)', fontFamily: 'monospace', textAlign: 'right', flexShrink: 0 }}>
                          {ev.lat?.toFixed(5)}, {ev.lng?.toFixed(5)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Mini map showing route with markers
function HistoryMap({ events, focusEvent, height = 340, resetKey }) {
  const mapRef = useRef(null);
  const leafRef = useRef(null);
  const markersRef = useRef({}); // event.id -> Leaflet marker, so clicking a timeline card can focus it without rebuilding the whole map
  const routeLayersRef = useRef([]); // [casing, main] polylines, extended incrementally rather than rebuilt
  const knownEventIdsRef = useRef(new Set()); // which events are already drawn, so new data only adds what's actually new
  const lastPlottedRef = useRef(null); // {lat,lng} of the last point actually included in the route line
  const currentMarkerRef = useRef(null); // the one marker that moves live, like the truck icon on Live Tracking
  const currentAnimCancelRef = useRef(null);
  const [matching, setMatching] = useState(false);

  const dotIcon = (ev) => {
    const isIdle = ev.type === 'idle';
    const color = isIdle ? '#f59e0b' : '#10b981';
    return window.L.divIcon({
      html: `<div style="width:10px;height:10px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 ${isIdle ? '8px ' + color : 'none'}"></div>`,
      className: '', iconSize: [10, 10], iconAnchor: [5, 5],
    });
  };
  const currentPositionIcon = () => window.L.divIcon({
    html: `<div style="position:relative;width:22px;height:22px;">
             <div style="position:absolute;inset:0;border-radius:50%;background:#2563eb33;animation:pulseRing 1.6s ease-out infinite;"></div>
             <div style="position:absolute;inset:4px;border-radius:50%;background:#2563eb;border:2.5px solid #fff;box-shadow:0 0 8px #2563eb;"></div>
           </div>`,
    className: '', iconSize: [22, 22], iconAnchor: [11, 11],
  });
  const popupHtml = (ev) => `<div style="font-family:DM Sans,sans-serif;font-size:12px;min-width:180px"><strong>${ev.type === 'idle' ? '⏸ Idle' : '▶ Moving'}</strong><br/><span style="color:#9ca3af">${displayAddress(ev)}</span><br/><span style="color:#9ca3af">${formatTime(ev.timestamp)}</span></div>`;

  // Full (re)initialization — only runs when actually switching trucks or
  // date filters (resetKey), NOT every time a new entry quietly arrives.
  // That's the key difference from before: a new live entry used to tear
  // down and rebuild the entire map (all markers, full re-route-matching),
  // which is exactly what caused the visible "refresh" flicker.
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    function init(L) {
      if (leafRef.current) { leafRef.current.remove(); leafRef.current = null; }
      markersRef.current = {};
      routeLayersRef.current = [];
      knownEventIdsRef.current = new Set();
      lastPlottedRef.current = null;
      currentMarkerRef.current = null;
      if (currentAnimCancelRef.current) { currentAnimCancelRef.current(); currentAnimCancelRef.current = null; }

      const validEvents = events.filter(e => e.lat && e.lng);

      // Create the map right away even with zero events — a truck being
      // viewed for the first time often has nothing yet until the
      // auto-sync effect finishes pulling its history a moment later.
      // The map needs to already exist by the time that data lands, or
      // the incremental sync effect below (which only ever ADDS to an
      // existing map, it never creates one) has nothing to draw onto —
      // that gap was exactly why the route/markers could silently never
      // appear at all for a truck with no history at the moment it was
      // first selected.
      const initialCenter = validEvents.length > 0 ? [validEvents[0].lat, validEvents[0].lng] : [14.5995, 120.9842];
      const map = L.map(mapRef.current, { center: initialCenter, zoom: validEvents.length > 0 ? 12 : 6 });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '©OpenStreetMap contributors', maxZoom: 19 }).addTo(map);
      leafRef.current = map;

      if (validEvents.length === 0) return; // map exists now; the incremental effect will draw everything once data arrives

      const bounds = validEvents.map(e => [e.lat, e.lng]);
      const sorted = [...validEvents].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Every event except the newest gets a plain historical dot; the
      // newest gets the special pulsing "current position" marker —
      // that's the one piece that will visibly move on future updates.
      sorted.forEach((ev, i) => {
        knownEventIdsRef.current.add(ev.id);
        const isLast = i === sorted.length - 1;
        if (isLast) return;
        const marker = L.marker([ev.lat, ev.lng], { icon: dotIcon(ev) }).addTo(map).bindPopup(popupHtml(ev));
        markersRef.current[ev.id] = marker;
      });

      const last = sorted[sorted.length - 1];
      const liveMarker = L.marker([last.lat, last.lng], { icon: currentPositionIcon(), zIndexOffset: 1000 }).addTo(map).bindPopup(popupHtml(last));
      markersRef.current[last.id] = liveMarker;
      currentMarkerRef.current = liveMarker;
      lastPlottedRef.current = { lat: last.lat, lng: last.lng };

      if (bounds.length > 1) map.fitBounds(bounds, { padding: [30, 30] });

      // Draw an immediate rough line (simplified, jitter-filtered) so
      // there's something on screen right away, then replace it with the
      // properly road-matched path once that comes back.
      if (sorted.length > 1) {
        const rough = simplifyPathForLine(sorted);
        const roughPoints = rough.length > 1 ? rough : sorted; // safety net: never end up with zero visible line
        const line = L.polyline(roughPoints.map(e => [e.lat, e.lng]), { color: '#93c5fd', weight: 3, opacity: 0.6, dashArray: '4,6', lineCap: 'round', lineJoin: 'round' }).addTo(map);
        routeLayersRef.current.push(line);

        setMatching(true);
        matchToRoad(sorted).then(segments => {
          if (cancelled || !leafRef.current) return;
          setMatching(false);
          if (!segments) return; // matching failed — keep the rough fallback line as-is

          // Replace the rough line with the real, road-snapped path —
          // styled like a turn-by-turn nav route: a soft light "casing"
          // underneath a solid, vibrant blue line on top, both with
          // rounded joins so it reads as one clean continuous path.
          routeLayersRef.current.forEach(l => leafRef.current.removeLayer(l));
          const casing = L.polyline([], { color: '#ffffff', weight: 9, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }).addTo(leafRef.current);
          const main = L.polyline([], { color: '#2563eb', weight: 5, opacity: 1, lineCap: 'round', lineJoin: 'round' }).addTo(leafRef.current);
          segments.forEach(seg => { seg.forEach(p => { casing.addLatLng(p); main.addLatLng(p); }); });
          routeLayersRef.current = [casing, main];
        });
      }
    }

    const L = window.L;
    if (!L) {
      const existing = document.querySelector('script[src*="leaflet.js"]');
      if (!existing) {
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        s.crossOrigin = 'anonymous';
        s.onload = () => init(window.L);
        document.head.appendChild(s);
      } else {
        existing.addEventListener('load', () => init(window.L));
      }
    } else {
      init(L);
    }

    return () => {
      cancelled = true;
      if (currentAnimCancelRef.current) { currentAnimCancelRef.current(); currentAnimCancelRef.current = null; }
      if (leafRef.current) { leafRef.current.remove(); leafRef.current = null; }
    };
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Incremental sync — runs whenever new data arrives. Handles two
  // distinct cases:
  //  - Cold start: the map exists but nothing's been drawn on it yet
  //    (e.g. auto-sync just pulled a truck's very first batch of history)
  //    — draws the whole batch at once: dots for everything except the
  //    latest point, the pulsing "current position" marker at the latest,
  //    and the full road-matched route through all of it, then fits the
  //    map to that data.
  //  - Normal case: the map already has a current marker — treats
  //    whatever's new as a continuation, drawing a dot for each newly-
  //    superseded point and animating the pulsing marker along the road
  //    to the latest one. No rebuild, no flicker — this is what makes it
  //    behave like the truck icon on Live Tracking instead of a
  //    page-style refresh.
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current) return;

    const validEvents = events.filter(e => e.lat && e.lng);
    const sorted = [...validEvents].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const newOnes = sorted.filter(e => !knownEventIdsRef.current.has(e.id));
    if (newOnes.length === 0) return;

    newOnes.forEach(ev => knownEventIdsRef.current.add(ev.id));

    if (!currentMarkerRef.current) {
      // Cold start — draw the entire first batch at once, same shape as
      // the original full-init logic, just triggered here instead of at
      // mount time (since the map may have been created before any data
      // existed yet — see the init effect above).
      newOnes.forEach((ev, i) => {
        if (i === newOnes.length - 1) return; // last one becomes the live marker below, not a plain dot
        const marker = L.marker([ev.lat, ev.lng], { icon: dotIcon(ev) }).addTo(leafRef.current).bindPopup(popupHtml(ev));
        markersRef.current[ev.id] = marker;
      });

      const last = newOnes[newOnes.length - 1];
      const liveMarker = L.marker([last.lat, last.lng], { icon: currentPositionIcon(), zIndexOffset: 1000 }).addTo(leafRef.current).bindPopup(popupHtml(last));
      markersRef.current[last.id] = liveMarker;
      currentMarkerRef.current = liveMarker;
      lastPlottedRef.current = { lat: last.lat, lng: last.lng };

      if (newOnes.length > 1) {
        leafRef.current.fitBounds(newOnes.map(e => [e.lat, e.lng]), { padding: [30, 30] });

        const rough = simplifyPathForLine(newOnes);
        const roughPoints = rough.length > 1 ? rough : newOnes;
        const line = L.polyline(roughPoints.map(e => [e.lat, e.lng]), { color: '#93c5fd', weight: 3, opacity: 0.6, dashArray: '4,6', lineCap: 'round', lineJoin: 'round' }).addTo(leafRef.current);
        routeLayersRef.current = [line];

        setMatching(true);
        matchToRoad(newOnes).then(segments => {
          setMatching(false);
          if (!segments || !leafRef.current) return;
          routeLayersRef.current.forEach(l => leafRef.current.removeLayer(l));
          const casing = L.polyline([], { color: '#ffffff', weight: 9, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }).addTo(leafRef.current);
          const main = L.polyline([], { color: '#2563eb', weight: 5, opacity: 1, lineCap: 'round', lineJoin: 'round' }).addTo(leafRef.current);
          segments.forEach(seg => { seg.forEach(p => { casing.addLatLng(p); main.addLatLng(p); }); });
          routeLayersRef.current = [casing, main];
        });
      } else {
        leafRef.current.setView([last.lat, last.lng], 14);
      }
      return;
    }

    // Normal case — the map already had a current marker; whatever's new
    // is a continuation. Draw a dot for every newly-superseded point
    // (there's usually just one, but handle several arriving in the same
    // tick too), then animate the pulsing marker along the road to
    // whichever one is now the latest.
    newOnes.slice(0, -1).forEach(ev => {
      const dot = L.marker([ev.lat, ev.lng], { icon: dotIcon(ev) }).addTo(leafRef.current).bindPopup(popupHtml(ev));
      markersRef.current[ev.id] = dot;
    });
    const prevPos = currentMarkerRef.current.getLatLng();
    const prevEvent = { lat: prevPos.lat, lng: prevPos.lng, type: 'idle', timestamp: new Date().toISOString(), id: 'prev-' + Date.now() };
    L.marker([prevPos.lat, prevPos.lng], { icon: dotIcon(prevEvent) }).addTo(leafRef.current).bindPopup(popupHtml(prevEvent));

    const newest = newOnes[newOnes.length - 1];

    if (lastPlottedRef.current && routeLayersRef.current.length === 2) {
      const [casing, main] = routeLayersRef.current;
      getRoute({ lat: lastPlottedRef.current.lat, lng: lastPlottedRef.current.lng }, { lat: newest.lat, lng: newest.lng })
        .then(route => {
          const path = route?.geometry?.length > 1 ? route.geometry : [[lastPlottedRef.current.lat, lastPlottedRef.current.lng], [newest.lat, newest.lng]];
          path.forEach(p => { casing.addLatLng(p); main.addLatLng(p); });
        })
        .catch(() => {
          casing.addLatLng([newest.lat, newest.lng]);
          main.addLatLng([newest.lat, newest.lng]);
        });
    }
    lastPlottedRef.current = { lat: newest.lat, lng: newest.lng };

    const cur = currentMarkerRef.current.getLatLng();
    currentMarkerRef.current.setPopupContent(popupHtml(newest));
    if (currentAnimCancelRef.current) currentAnimCancelRef.current();
    getRoute({ lat: cur.lat, lng: cur.lng }, { lat: newest.lat, lng: newest.lng })
      .then(route => {
        const path = route?.geometry?.length > 1 ? route.geometry : [[cur.lat, cur.lng], [newest.lat, newest.lng]];
        currentAnimCancelRef.current = animateMarkerAlongPath(currentMarkerRef.current, path, 3000);
      })
      .catch(() => currentMarkerRef.current.setLatLng([newest.lat, newest.lng]));
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pan/zoom to a specific event's marker and pop it open — triggered by
  // clicking a card in the timeline below — without rebuilding the map.
  useEffect(() => {
    if (!focusEvent || !leafRef.current) return;
    const marker = markersRef.current[focusEvent.id];
    if (marker) {
      leafRef.current.setView(marker.getLatLng(), 16, { animate: true });
      marker.openPopup();
    } else if (focusEvent.lat != null && focusEvent.lng != null) {
      // Marker not found (e.g. events list changed) — still pan to the coordinates
      leafRef.current.setView([focusEvent.lat, focusEvent.lng], 16, { animate: true });
    }
  }, [focusEvent]);

  return (
    <div style={{ height, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      {events.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bgInput)', color: 'var(--textFaint)', fontSize: 12, pointerEvents: 'none' }}>
          Checking for history...
        </div>
      )}
      {matching && (
        <div style={{ position: 'absolute', bottom: 10, left: 10, zIndex: 1000, background: 'var(--bgModal)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', fontSize: 11, color: 'var(--textMuted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
          Tracing road path...
        </div>
      )}
    </div>
  );
}

export default function TravelHistory() {
  const { trucks, gpsDevices, getHistoryForTruck, clearHistoryForTruck, travelHistory, refreshTravelHistory, loadTravelHistoryForTruck, addTravelHistoryEntryFromStream, bulkAddTravelHistory, showNotification } = useApp();
  const { isMobile, isDesktop } = useResponsive();
  const [selectedTruckId, setSelectedTruckId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [confirmClear, setConfirmClear] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const [focusedEvent, setFocusedEvent] = useState(null); // event clicked in the timeline, focused on the map above
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [autoSyncProgress, setAutoSyncProgress] = useState(null);
  const autoSyncingRef = useRef(false); // read by the 5s refresh below so it can stand down while a sync is saving
  const mapSectionRef = useRef(null);

  const handleTimelineEventClick = (ev) => {
    setFocusedEvent(ev);
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Auto-sync: the moment a truck is selected here, pull its historical
  // positions from the GPS server and reconstruct any idle/moving
  // transitions it's missing — the exact same logic as the manual "Sync
  // Travel History" button in Settings, just triggered automatically and
  // scoped to only this one truck instead of requiring a trip to Settings
  // and syncing everything. Incremental — resumes from this truck's most
  // recent existing entry, so re-selecting a truck you already viewed
  // recently is fast (usually nothing new to fetch at all).
  useEffect(() => { autoSyncingRef.current = autoSyncing; }, [autoSyncing]);

  // The auto-sync below loads a truck's history itself, but it bails out
  // early for trucks with no linked GPS device — those still need their saved
  // history loaded from the server when selected, or they'd only show
  // whatever happened to fit in the capped combined list.
  useEffect(() => {
    if (!selectedTruckId) return;
    const truck = trucks.find(t => t.id === selectedTruckId);
    const gps = truck ? gpsDevices.find(g => g.id === truck.gpsDeviceId) : null;
    if (truck && gps?.traccarDeviceId) return; // handled by the auto-sync effect
    loadTravelHistoryForTruck(selectedTruckId).catch(() => { /* the periodic refresh will retry */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruckId]);

  useEffect(() => {
    if (!selectedTruckId) return;
    const truck = trucks.find(t => t.id === selectedTruckId);
    const gps = truck ? gpsDevices.find(g => g.id === truck.gpsDeviceId) : null;
    if (!truck || !gps?.traccarDeviceId) return; // nothing to sync against without a linked, registered GPS device

    let cancelled = false;
    (async () => {
      setAutoSyncing(true);
      setAutoSyncProgress({ phase: 'positions' });
      try {
        // Load this truck's full saved history FIRST — it's what the page
        // displays, and it's what decides where to resume from. It must come
        // from the server per-truck, not the in-memory list, which can be
        // missing a truck's older entries (the combined list is capped across
        // all trucks). Doing it before the GPS-connection check means the
        // history still shows even when the GPS server isn't connected. If it
        // can't be loaded, stop rather than guess: with no known history the
        // sync would re-import 30 days and write duplicates into the database.
        let fetchedForTruck;
        try {
          fetchedForTruck = await loadTravelHistoryForTruck(truck.id);
        } catch (loadErr) {
          throw new Error(`Couldn't load ${truck.plateNumber}'s saved history, so auto-sync was skipped to avoid importing duplicates (${loadErr.message})`);
        }
        if (cancelled) return;

        // This page doesn't otherwise touch the GPS server connection at
        // all — unlike Live Tracking/GPS Devices/Settings, which each
        // bootstrap it on their own mount. Someone navigating straight
        // here without visiting one of those pages first in the same
        // session would have an empty, unauthenticated connection and get
        // a 401 the moment this tries to fetch anything — so make sure
        // it's loaded here too before proceeding. Cheap and safe to call
        // even if something else already did this.
        await loadCredentialsFromBackend();
        if (!TraccarConfig.isConfigured()) {
          throw new Error('GPS server isn\'t connected yet — set it up in Settings, or ask your admin to.');
        }

        const existingForTruck = [...travelHistory.filter(h => h.truckId === truck.id), ...fetchedForTruck];
        const syncWarnings = [];
        const transitions = await syncOneTruck(truck, gps.traccarDeviceId, existingForTruck, (phase, extra) => {
          if (phase === 'warning') { syncWarnings.push(extra.message); return; }
          if (!cancelled) setAutoSyncProgress({ phase, ...extra });
        });
        if (cancelled) return;
        if (syncWarnings.length > 0) showNotification(`${truck.plateNumber}: ${syncWarnings.join(' · ')}`, 'error');
        if (transitions.length > 0) {
          try {
            const { insertedCount, skippedCount } = await bulkAddTravelHistory(transitions);
            if (insertedCount > 0 || skippedCount > 0) {
              showNotification(
                `Synced ${insertedCount} new entr${insertedCount === 1 ? 'y' : 'ies'} for ${truck.plateNumber}` +
                (skippedCount > 0 ? ` (${skippedCount} skipped — couldn't be saved)` : '')
              );
            }
          } catch (saveErr) {
            // Stopped early (server unreachable/overloaded) — some batches may still have saved
            const done = saveErr.insertedCount || 0;
            showNotification(
              done > 0
                ? `Saved ${done} of ${transitions.length} entries for ${truck.plateNumber} before the server stopped responding — sync again shortly to finish.`
                : `Auto-sync failed: ${saveErr.message}`,
              'error'
            );
          }
        }
      } catch (err) {
        if (!cancelled) showNotification(`Auto-sync failed: ${err.message}`, 'error');
      } finally {
        if (!cancelled) { setAutoSyncing(false); setAutoSyncProgress(null); }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruckId]);

  // Real-time updates: the backend's background tracker (see
  // services/travelHistoryTracker.js) records history independently of
  // any browser, and pushes each new entry over this stream the instant
  // it's written — a bonus on top of the baseline 5-second poll below for
  // near-instant updates when this connection is up. Some hosting setups
  // (notably cPanel's Node.js app manager, which runs on Phusion Passenger
  // behind Apache) enforce a short idle-connection timeout that can kill a
  // long-lived SSE stream every few seconds regardless of the server's
  // own heartbeat — this doesn't try to detect or work around that here
  // anymore, since the poll below guarantees new data shows up quickly
  // either way; this stream just makes it feel instant when it's working.
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const es = new EventSource(`${BASE_URL}/travel-history-stream?token=${encodeURIComponent(token)}`);

    es.onopen = () => setLiveConnected(true);
    es.onerror = () => setLiveConnected(false);

    es.addEventListener('entry', (e) => {
      try {
        const entry = JSON.parse(e.data);
        addTravelHistoryEntryFromStream(entry);
        setLastRefreshed(new Date());
      } catch {
        // ignore a malformed push — the periodic poll below will catch up regardless
      }
    });

    return () => es.close();
  }, [addTravelHistoryEntryFromStream]);

  // Polls every few seconds as the reliable baseline for "new data shows
  // up quickly" — the live stream above is a bonus on top of this for
  // near-instant updates when it's working, but this doesn't depend on
  // that connection succeeding at all. Given how spotty long-lived SSE
  // connections can be on some hosting setups (see the note above), this
  // guarantees new travel history entries always appear within a few
  // seconds regardless, rather than only after detecting the stream has
  // failed repeatedly.
  useEffect(() => {
    // Nothing selected = nothing on screen that needs refreshing.
    if (!selectedTruckId) return;
    let cancelled = false;
    const tick = async () => {
      // A sync is uploading batches right now — don't add a fetch on top of it
      // every 5 seconds. Newly saved entries are merged into local state as
      // each batch lands, and this resumes as soon as the sync ends.
      if (autoSyncingRef.current) return;
      setRefreshing(true);
      try {
        // Just the newest rows for the selected truck, MERGED into what's
        // already shown — cheap, and never drops anything already loaded.
        await loadTravelHistoryForTruck(selectedTruckId, 200);
        if (!cancelled) setLastRefreshed(new Date());
      } catch {
        // transient — try again next tick
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    };
    const interval = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [loadTravelHistoryForTruck, selectedTruckId]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      if (selectedTruckId) await loadTravelHistoryForTruck(selectedTruckId);
      else await refreshTravelHistory();
      setLastRefreshed(new Date());
    } catch (err) {
      showNotification(`Couldn't refresh: ${err.message}`, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Clear any focused map pin when switching trucks — an old focus target
  // from a different truck's list shouldn't linger.
  useEffect(() => { setFocusedEvent(null); }, [selectedTruckId]);

  const trackedTrucks = trucks.filter(t =>
    t.gpsDeviceId || travelHistory.some(h => h.truckId === t.id)
  );

  const selectedTruck = trucks.find(t => t.id === selectedTruckId);

  let events = selectedTruckId ? getHistoryForTruck(selectedTruckId, dateFrom, dateTo) : [];
  if (filterType !== 'all') events = events.filter(e => e.type === filterType);

  const idleCount = events.filter(e => e.type === 'idle').length;
  const moveCount = events.filter(e => e.type === 'moving').length;
  const gps = selectedTruck ? gpsDevices.find(g => g.id === selectedTruck.gpsDeviceId) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Live refresh status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'var(--textFaint)', padding: '0 2px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: refreshing ? '#f59e0b' : liveConnected ? '#10b981' : '#3b82f6',
            boxShadow: refreshing ? 'none' : `0 0 6px ${liveConnected ? '#10b981' : '#3b82f6'}`,
          }} />
          {refreshing
            ? 'Refreshing...'
            : liveConnected
              ? `Live — connected, updated ${lastRefreshed.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
              : `Updating every 5s, last updated ${lastRefreshed.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={refreshing}
          style={{ background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--textMuted)', fontSize: 11, fontWeight: 600, padding: '4px 10px', cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.6 : 1 }}
        >
          ↻ Refresh now
        </button>
      </div>

      {autoSyncing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 9, fontSize: 12, color: 'var(--textSecondary)' }}>
          <span style={{ width: 12, height: 12, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
          {autoSyncProgress?.phase === 'geocoding'
            ? `Filling in location names... (${autoSyncProgress.current}/${autoSyncProgress.total})`
            : autoSyncProgress?.phase === 'positions' && autoSyncProgress.total > 1
              ? `Checking the GPS server for anything missed... (day ${Math.min(autoSyncProgress.current + 1, autoSyncProgress.total)} of ${autoSyncProgress.total})`
              : 'Checking the GPS server for anything missed...'}
        </div>
      )}

      {/* Filters */}
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>Select Truck</div>
            <Select
              value={selectedTruckId} onChange={setSelectedTruckId} placeholder="— Choose a truck —"
              options={trackedTrucks.map(t => ({ value: t.id, label: `${t.plateNumber} — ${t.model} (${t.driver || 'Unassigned'})` }))}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>From Date</div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9, padding: '10px 12px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif' }} />
            </div>
            {isMobile && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>To Date</div>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9, padding: '10px 12px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif' }} />
              </div>
            )}
          </div>
          {!isMobile && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>To Date</div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9, padding: '10px 12px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif' }} />
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>Show</div>
            <Select value={filterType} onChange={setFilterType} options={[{ value: 'all', label: 'All Events' }, { value: 'idle', label: 'Idle Only' }, { value: 'moving', label: 'Moving Only' }]} />
          </div>
          {selectedTruckId && <Button variant="danger" onClick={() => setConfirmClear(true)} style={{ alignSelf: 'flex-end', width: isMobile ? '100%' : 'auto' }}>Clear</Button>}
        </div>
      </Card>

      {/* Truck info bar */}
      {selectedTruck && (
        <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, padding: isMobile ? '14px 16px' : '14px 20px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 10, height: 44, borderRadius: 3, background: selectedTruck.color, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: 'var(--textPrimary)' }}>{selectedTruck.plateNumber}</div>
              <div style={{ fontSize: 12, color: 'var(--textMuted)' }}>{selectedTruck.model} {selectedTruck.year} · {selectedTruck.driver}</div>
            </div>
            {isMobile && (() => { const s = TRUCK_STATUSES[selectedTruck.status] || TRUCK_STATUSES.offline; return <Badge color={s.color} bg={s.bg}>{s.label}</Badge>; })()}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, textAlign: 'center', flex: isMobile ? 'none' : 1 }}>
            <div><div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: 'var(--textPrimary)' }}>{events.length}</div><div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Events</div></div>
            <div><div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: '#f59e0b' }}>{idleCount}</div><div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Idle Stops</div></div>
            <div><div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: '#10b981' }}>{moveCount}</div><div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Movements</div></div>
          </div>
          {!isMobile && (
            <>
              {gps && <Badge color="#8b5cf6">{gps.deviceId}</Badge>}
              {(() => { const s = TRUCK_STATUSES[selectedTruck.status] || TRUCK_STATUSES.offline; return <Badge color={s.color} bg={s.bg}>{s.label}</Badge>; })()}
            </>
          )}
        </div>
      )}

      {/* Timeline (left) + Map (right) */}
      {selectedTruckId && (
        <div style={{
          display: isDesktop ? 'grid' : 'flex',
          gridTemplateColumns: isDesktop ? 'minmax(0, 1fr) minmax(0, 2fr)' : undefined,
          flexDirection: isDesktop ? undefined : 'column',
          gap: 16, alignItems: 'flex-start',
        }}>
          <Card title={`Travel Timeline${selectedTruck ? ' — ' + selectedTruck.plateNumber : ''}`} style={{ minWidth: 0 }}>
            {events.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
                <div style={{ fontSize: 14, color: 'var(--textMuted)', marginBottom: 6 }}>No travel history found</div>
                <div style={{ fontSize: 12, color: 'var(--textFaint)' }}>
                  {dateFrom || dateTo ? 'Try adjusting the date filters.' : 'Travel events will appear here as the truck moves.'}
                </div>
              </div>
            ) : (
              <Timeline events={events} trucks={trucks} isMobile={isMobile} onEventClick={handleTimelineEventClick} selectedEventId={focusedEvent?.id} />
            )}
          </Card>

          <div ref={mapSectionRef} style={{ width: '100%', position: isDesktop ? 'sticky' : 'static', top: isDesktop ? 16 : undefined }}>
            <HistoryMap events={events} focusEvent={focusedEvent} height={isDesktop ? 620 : 340} resetKey={`${selectedTruckId}-${dateFrom}-${dateTo}`} />
          </div>
        </div>
      )}

      {!selectedTruckId && (
        <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 14, padding: '64px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🗺️</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 20, color: 'var(--textPrimary)', marginBottom: 8 }}>Travel History</div>
          <div style={{ fontSize: 14, color: 'var(--textMuted)', maxWidth: 400, margin: '0 auto' }}>
            Select a truck above to view its complete travel timeline — including every idle stop and movement with timestamps and locations.
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => clearHistoryForTruck(selectedTruckId)}
        title="Clear Travel History"
        message={`This will permanently delete all travel history for ${selectedTruck?.plateNumber}. This cannot be undone.`}
      />
    </div>
  );
}
